import * as React from 'react'
import { cn } from '@/lib/utils'

type BaseInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
>

export interface SwitchProps extends BaseInputProps {
  label?: React.ReactNode
  size?: 'sm' | 'md' | 'lg'
}

const sizeMap = {
  sm: { track: 'h-4 w-7', knob: 'h-3 w-3', translate: 'translate-x-3' },
  md: { track: 'h-5 w-9', knob: 'h-4 w-4', translate: 'translate-x-4' },
  lg: { track: 'h-6 w-11', knob: 'h-5 w-5', translate: 'translate-x-5' },
} as const

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      className,
      label,
      size = 'md',
      disabled,
      checked,
      defaultChecked,
      ...rest
    },
    ref
  ) => {
    const [internal, setInternal] = React.useState<boolean>(!!defaultChecked)
    const isOn = checked ?? internal
    const s = sizeMap[size]

    const autoId = React.useId()
    const id = rest.id ?? autoId

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (checked === undefined) setInternal(e.target.checked)
      rest.onChange?.(e)
    }

    return (
      <label
        htmlFor={id}
        className={cn(
          'inline-flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200',
          disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
          className
        )}
      >
        <span
          className={cn(
            'relative inline-flex shrink-0 rounded-full transition-colors',
            // O anel de foco tem que aparecer NO TRILHO: o input real é
            // `opacity-0` e recebia o anel invisível, deixando o controle sem
            // indicador de foco (A11Y-FOCUS-01).
            'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary-500',
            s.track,
            isOn ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-700'
          )}
        >
          <input
            ref={ref}
            id={id}
            type="checkbox"
            role="switch"
            aria-checked={isOn}
            checked={isOn}
            disabled={disabled}
            onChange={onChange}
            className="peer absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
            {...rest}
          />
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute top-1/2 left-0.5 -translate-y-1/2 rounded-full bg-white shadow-sm transition-transform',
              s.knob,
              isOn && s.translate
            )}
          />
        </span>
        {label && <span className="select-none">{label}</span>}
      </label>
    )
  }
)

Switch.displayName = 'Switch'
